/* ============================================================================
 * hfb-bubbles.js — <hfb-bubbles>, the jacuzzi-bubbles background (origin: hfb).
 *
 * Task 12483. A port of the client-supplied bubbles-background-demo.html script: the
 * drawing, the motion and every CONFIG value are the reference's, unchanged. Only the
 * colours differ — bubble = --htw-water, rim tint = --htw-purple, a soft white glint,
 * peak opacity 0.5 — read from the theme's CSS custom properties, never hard-coded.
 *
 * Rendered by snippets/hfb-bubbles.liquid (via hfb-section-deco) on Paper sections only.
 * Plain 2D canvas, no libraries. The element fills its section (CSS: inset 0, z-index 0,
 * pointer-events none, aria-hidden), so content above it stays readable and clickable.
 *
 * Performance: sections on screen at start draw at once; the rest set themselves up when
 * they come within 200px. The animation runs only while the section is in view. This
 * IntersectionObserver gates an animation loop, it is NOT a reveal — reveals stay on the
 * one hfb-motion.js observer.
 * Reduced motion: drawn once, still, no popping. No 2D canvas: renders nothing.
 * disconnectedCallback tears everything down, so a theme-editor re-render never leaves a
 * stray or duplicated canvas.
 * ========================================================================== */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CONFIG = {
  density: 1 / 11000,      // bubbles per square pixel of section
  maxBubbles: 70,
  minRadius: 1.5,          // px
  maxRadius: 11,           // px; most bubbles are small, a few are big
  riseSpeed: 1,            // 1 is a slow jacuzzi pace
  wobble: 1,               // how much bubbles sway side to side
  pushRadius: 90,          // px around the cursor that bubbles drift away from
  pushStrength: 1,
  popReach: 6,             // px beyond a bubble's edge the cursor pops it
  naturalPops: 0.015,      // chance per second that a bubble pops on its own
  respawnDelay: [0.6, 3]   // seconds before a popped bubble rises again
};
const PEAK_OPACITY = 0.5;

function rand(a, b) { return a + Math.random() * (b - a); }
// Distance from a point to the segment a-b (the cursor's path this frame).
function distToPath(x, y, a, b) {
  const vx = b.x - a.x, vy = b.y - a.y, len2 = vx * vx + vy * vy;
  const t = len2 ? Math.max(0, Math.min(1, ((x - a.x) * vx + (y - a.y) * vy) / len2)) : 0;
  const dx = x - a.x - vx * t, dy = y - a.y - vy * t;
  return Math.sqrt(dx * dx + dy * dy);
}
function smooth(e0, e1, x) {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
// Resolve a CSS colour (hex, rgb(), named …) to [r, g, b] through the canvas parser.
function toRgb(ctx, value, fallback) {
  ctx.fillStyle = fallback;
  if (value) ctx.fillStyle = value.trim();
  const s = ctx.fillStyle;
  if (s[0] === '#') { const n = parseInt(s.slice(1, 7), 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; }
  const m = s.match(/[\d.]+/g) || [0, 0, 0];
  return [+m[0], +m[1], +m[2]];
}
function rgba(c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')'; }

class HfbBubbles extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    const section = this.closest('.hfb-s') || this.parentElement;
    if (!section) return;
    const host = this;
    let canvas, ctx;
    let film, rim, glint, tint, solid;
    let w = 0, h = 0, dpr = 1;
    const bubbles = [], pops = [];
    let time = 0, raf = 0, last = 0;
    let pointer = null, lastPointer = null;
    const pointerVel = { x: 0, y: 0 };
    let ready = false;

    function spawn(b, anywhere) {
      const t = Math.pow(Math.random(), 2);   // mostly small, a few big
      b.r = CONFIG.minRadius + (CONFIG.maxRadius - CONFIG.minRadius) * t;
      b.x = Math.random() * w;
      b.y = anywhere ? Math.random() * h : h + b.r + Math.random() * 30;
      b.speed = (12 + b.r * 3) * rand(0.8, 1.2);   // bigger bubbles rise faster
      b.phase = Math.random() * Math.PI * 2;
      b.freq = rand(1, 2.4);
      b.sway = (1.5 + b.r * 0.6) * CONFIG.wobble;
      b.alpha = rand(0.45, 1);
      b.vx = b.vy = 0;      // extra drift from the cursor, dies down over time
      b.wait = 0;           // > 0 while popped, counting down to respawn
      return b;
    }

    // Where a bubble is drawn: its path plus a gentle side-to-side sway.
    function bubbleX(b) { return b.x + Math.sin(time * b.freq + b.phase) * b.sway; }

    function pop(b) {
      const x = bubbleX(b), drops = [];
      const n = Math.min(7, 3 + Math.round(b.r / 2));
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2, s = rand(25, 60) * (0.6 + b.r / 10);
        drops.push({ x: x, y: b.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 15, size: rand(0.6, 1.3) });
      }
      pops.push({ x: x, y: b.y, r: b.r, age: 0, alpha: b.alpha, drops: drops });
      b.wait = rand(CONFIG.respawnDelay[0], CONFIG.respawnDelay[1]);
    }

    function resize() {
      w = section.clientWidth;
      h = section.clientHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      const want = Math.min(CONFIG.maxBubbles, Math.round(w * h * CONFIG.density));
      while (bubbles.length < want) bubbles.push(spawn({}, true));
      bubbles.length = want;
      bubbles.forEach(function (b) { if (b.x > w) b.x = Math.random() * w; });
    }

    function update(dt, from) {
      time += dt;
      const damp = Math.exp(-dt * 2.5);
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        if (b.wait > 0) {
          b.wait -= dt;
          if (b.wait <= 0) spawn(b, false);
          continue;
        }
        b.y -= b.speed * CONFIG.riseSpeed * dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.vx *= damp;
        b.vy *= damp;
        if (b.y < -b.r * 2) { spawn(b, false); continue; }
        if (b.y < h * 0.9 && Math.random() < CONFIG.naturalPops * dt) { pop(b); continue; }

        // The cursor pops any bubble its path touches, and gently nudges
        // the ones around it away (a little harder while it's moving).
        if (pointer) {
          const bx = bubbleX(b);
          if (distToPath(bx, b.y, from || pointer, pointer) < b.r + CONFIG.popReach) { pop(b); continue; }
          const dx = bx - pointer.x, dy = b.y - pointer.y;
          const d = Math.sqrt(dx * dx + dy * dy) || 0.001;
          if (d < CONFIG.pushRadius) {
            const speed = Math.sqrt(pointerVel.x * pointerVel.x + pointerVel.y * pointerVel.y);
            let f = 1 - d / CONFIG.pushRadius;
            f = f * f * CONFIG.pushStrength * dt;
            const push = 70 + Math.min(speed, 800) * 0.25;
            b.vx += (dx / d * push + pointerVel.x * 0.3) * f;
            b.vy += (dy / d * push + pointerVel.y * 0.3) * f;
          }
        }
      }
      for (let j = pops.length - 1; j >= 0; j--) {
        const p = pops[j];
        p.age += dt;
        p.drops.forEach(function (o) {
          o.x += o.vx * dt;
          o.y += o.vy * dt;
          o.vy += 220 * dt;   // droplets fall back a little
        });
        if (p.age > 0.45) pops.splice(j, 1);
      }
    }

    function draw() {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 1;
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        if (b.wait > 0) continue;
        // Fade in just above the bottom edge and out towards the top.
        const a = PEAK_OPACITY * b.alpha * smooth(0, h * 0.12, h - b.y) * smooth(0, h * 0.18, b.y);
        if (a <= 0.01) continue;
        const x = bubbleX(b), y = b.y;
        ctx.globalAlpha = a;
        if (b.r < 2.5) {
          // The smallest bubbles read as bright specks.
          ctx.fillStyle = solid;
          ctx.globalAlpha = a * 0.7;
          ctx.beginPath();
          ctx.arc(x, y, b.r * 0.8, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }
        // A slight wobble in shape, as bubbles do when they rise.
        const squash = 1 + 0.06 * Math.sin(time * b.freq * 2.3 + b.phase);
        const rx = b.r * squash, ry = b.r / squash;
        ctx.beginPath();
        ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
        ctx.fillStyle = film;       // a faint film of water
        ctx.fill();
        ctx.strokeStyle = rim;      // the thin edge
        ctx.stroke();
        ctx.beginPath();            // brand colour catching the lower edge
        ctx.ellipse(x, y, rx, ry, 0, -0.2, Math.PI * 0.7);
        ctx.strokeStyle = tint;
        ctx.stroke();
        ctx.beginPath();            // highlight on the upper left
        ctx.arc(x - rx * 0.4, y - ry * 0.42, Math.max(0.8, b.r * 0.22), 0, Math.PI * 2);
        ctx.fillStyle = glint;
        ctx.fill();
      }
      // Pops: a quick ring that spreads and fades, and a few droplets.
      for (let j = 0; j < pops.length; j++) {
        const p = pops[j], t = p.age / 0.45;
        const fade = PEAK_OPACITY * p.alpha * (1 - t);
        ctx.globalAlpha = fade * 0.9;
        ctx.strokeStyle = solid;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * (1 + t * 0.8), 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = solid;
        p.drops.forEach(function (o) {
          ctx.globalAlpha = fade;
          ctx.beginPath();
          ctx.arc(o.x, o.y, o.size, 0, Math.PI * 2);
          ctx.fill();
        });
      }
      ctx.globalAlpha = 1;
    }

    function frame(now) {
      raf = requestAnimationFrame(frame);
      const dt = Math.min((now - last) / 1000, 1 / 20);
      last = now;
      if (dt <= 0) return;
      if (pointer && lastPointer) {
        // Cursor speed in px/s, eased so a single jump doesn't spike it.
        const k = 1 - Math.exp(-dt * 12);
        pointerVel.x += ((pointer.x - lastPointer.x) / dt - pointerVel.x) * k;
        pointerVel.y += ((pointer.y - lastPointer.y) / dt - pointerVel.y) * k;
      } else {
        pointerVel.x = pointerVel.y = 0;
      }
      const from = lastPointer;
      lastPointer = pointer;
      update(dt, from);
      draw();
    }

    function start() {
      if (reduceMotion || raf) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
    function stop() {
      cancelAnimationFrame(raf);
      raf = 0;
    }

    function onMove(e) {
      if (!raf) return;
      const r = section.getBoundingClientRect();
      pointer = { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    function onLeave() { pointer = lastPointer = null; }

    // Built the first time the section comes near the screen.
    function setup() {
      canvas = document.createElement('canvas');
      canvas.className = 'hfb-bubbles__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      ctx = canvas.getContext && canvas.getContext('2d');
      if (!ctx) { canvas = null; return false; }
      const cs = getComputedStyle(section);
      const water = toRgb(ctx, cs.getPropertyValue('--htw-water'), '#0099cb');
      const purple = toRgb(ctx, cs.getPropertyValue('--htw-purple'), '#6b1881');
      film = rgba(water, 0.07);
      rim = rgba(water, 0.6);
      glint = 'rgba(255,255,255,0.85)';
      tint = rgba(purple, 0.7);
      solid = rgba(water, 1);
      host.appendChild(canvas);
      resize();
      draw();

      host._ro = new ResizeObserver(function () {
        resize();
        if (!raf) draw();
      });
      host._ro.observe(section);

      if (!reduceMotion) {
        section.addEventListener('pointermove', onMove, { passive: true });
        section.addEventListener('pointerleave', onLeave);
      }
      return true;
    }

    // Sections on screen at load are drawn straight away, so the bubbles are
    // there on the first frame; the rest wait until they come near.
    const box = section.getBoundingClientRect();
    ready = box.top < window.innerHeight + 200 && box.bottom > -200 ? setup() : false;

    // Animate only while the section is on screen.
    host._io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        if (!ready) ready = setup();
        if (ready) start();
      } else {
        stop();
      }
    }, { rootMargin: '200px' });
    host._io.observe(section);

    host._teardown = function () {
      stop();
      if (host._io) host._io.disconnect();
      if (host._ro) host._ro.disconnect();
      host._io = host._ro = null;
      section.removeEventListener('pointermove', onMove);
      section.removeEventListener('pointerleave', onLeave);
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = ctx = null;
      bubbles.length = 0;
      pops.length = 0;
    };
  }

  disconnectedCallback() {
    if (this._teardown) this._teardown();
    this._teardown = null;
    this._on = false;
  }
}

if (!customElements.get('hfb-bubbles')) customElements.define('hfb-bubbles', HfbBubbles);
